const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5161') + '/api/v1';
const SERVER_OFFLINE_MESSAGE = 'AirFlyNow is temporarily unavailable. Please try again shortly.';

// In-memory access token — never stored in localStorage to prevent XSS token theft.
let _accessToken = null;
let _refreshPromise = null; // Prevent parallel refresh races.

const SENSITIVE_ERROR_PATTERNS = [
  /stripe/i,
  /secret[_\s-]?key/i,
  /environment variable/i,
  /not configured/i,
  /checkout session/i,
  /api key/i,
  /localhost/i,
  /credential/i,
  /access token/i,
  /signature/i,
  /webhook/i,
  /connection string/i,
];

/**
 * Returns a user-friendly error message based on HTTP status code.
 * Used for checkout and booking errors.
 */
const getFriendlyErrorMessage = (status) => {
  if (status >= 500) {
    return 'Our system is temporarily unavailable. Please try again in a moment.';
  } else if (status === 429) {
    return 'Too many requests. Please wait a moment and try again.';
  } else if (status === 408) {
    return 'The request timed out. Please check your connection and try again.';
  } else if (status === 403) {
    return 'You do not have permission to complete this booking.';
  } else if (status === 400) {
    return 'This flight is no longer available. Please search again for the latest options.';
  } else {
    return 'Unable to process your booking. Please try again or contact support.';
  }
};

/**
 * Sets the current user data in sessionStorage (cleared when tab closes)
 * sessionStorage is safer than localStorage for sensitive data as it's cleared
 * when the browser tab is closed, reducing XSS attack surface.
 */
const setCurrentUser = (user) => {
  if (user) {
    // Use sessionStorage instead of localStorage for better security
    // sessionStorage is cleared when tab closes, limiting XSS exposure
    sessionStorage.setItem('user', JSON.stringify({ 
      id: user.id, 
      fullName: user.fullName, 
      email: user.email 
    }));
  } else {
    sessionStorage.removeItem('user');
  }
};

/**
 * Gets the current user data from sessionStorage
 */
const getCurrentUser = () => {
  try {
    const userStr = sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

/**
 * Generates a unique trace ID for request correlation.
 * Falls back to a timestamp+random string in environments without crypto.randomUUID.
 */
const generateRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Decodes the payload of a JWT without verifying the signature.
 * Used only to derive UI hints (e.g. role) from the locally-issued token.
 * Server-side authorization is always enforced by the backend independently.
 */
const decodeJwtPayload = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const fetchWithOfflineHint = async (url, options = {}) => {
  const requestId = generateRequestId();
  const enriched = {
    ...options,
    headers: {
      ...options.headers,
      'X-Request-ID': requestId,
    },
  };
  try {
    return await fetch(url, enriched);
  } catch (error) {
    if (error instanceof TypeError) {
      const offlineError = new Error(SERVER_OFFLINE_MESSAGE);
      offlineError.isNetworkError = true;
      throw offlineError;
    }

    throw error;
  }
};

const getApiError = async (response, fallbackMessage) => {
  try {
    const payload = await response.json();
    return {
      message: payload?.message || payload?.error || fallbackMessage,
      code: payload?.code || null,
    };
  } catch {
    // Ignore JSON parse issues and use fallback.
  }

  return { message: fallbackMessage, code: null };
};

const containsSensitiveErrorDetails = (message) => SENSITIVE_ERROR_PATTERNS.some((pattern) => pattern.test(String(message || '')));

const buildApiError = async (response, fallbackMessage) => {
  const { message: rawMessage, code } = await getApiError(response, fallbackMessage);
  const message = containsSensitiveErrorDetails(rawMessage) ? fallbackMessage : rawMessage;
  const apiError = new Error(message);
  apiError.status = response.status;
  apiError.code = code;
  apiError.isSensitiveInternalError = containsSensitiveErrorDetails(rawMessage);
  return apiError;
};

export const isCriticalApiError = (error) => {
  const status = Number(error?.status);
  if (Number.isFinite(status) && status >= 500) {
    return true;
  }

  if (error?.isNetworkError) {
    return true;
  }

  if (error?.isSensitiveInternalError) {
    return true;
  }

  const message = String(error?.message || '').toLowerCase();
  return message.includes('cannot connect to airflynow server') || message.includes('failed to fetch');
};

/**
 * Attempts to get a new access token using the HttpOnly refresh token cookie.
 * Returns true if successful. Deduplicates concurrent refresh calls.
 */
const tryRefreshAccessToken = async () => {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return false;
      const data = await response.json();
      if (data.accessToken) {
        _accessToken = data.accessToken;
        if (data.user) {
          // Store user in sessionStorage (safer than localStorage)
          setCurrentUser(data.user);
        }
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
};

/**
 * Wraps fetch with Bearer token injection. On 401, attempts silent refresh once.
 */
const fetchWithAuth = async (url, options = {}) => {
  const makeRequest = (token) =>
    fetchWithOfflineHint(url, {
      ...options,
      credentials: 'include',
      headers: {
        ...options.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

  let response = await makeRequest(_accessToken);

  if (response.status === 401) {
    // Skip refresh entirely when the user is definitely a guest:
    // no in-memory token AND no stored user profile (set on login, cleared on logout).
    // This avoids a noisy 401 on /auth/refresh in the browser console for guest flows.
    const hadToken = Boolean(_accessToken);
    const mightHaveSession = hadToken || Boolean(getCurrentUser());
    if (!mightHaveSession) return response;

    const refreshed = await tryRefreshAccessToken();
    if (refreshed) {
      response = await makeRequest(_accessToken);
    } else if (hadToken) {
      // Refresh failed for an authenticated user — session is definitively expired.
      window.dispatchEvent(new CustomEvent('airfly:session-expired'));
    }
    // For a user who had a stored profile but no active token (e.g. account was deleted),
    // pass the 401 through so the caller can show a helpful "please sign in" message.
  }

  return response;
};

/**
 * Auth API calls
 */
export const authAPI = {
  register: async (fullName, email, password, referralCode = null) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fullName, email, password, referralCode }),
    });
    if (!response.ok) {
      throw await buildApiError(response, 'Registration failed. Please try again.');
    }
    return response.json();
  },

  login: async (email, password, rememberMe = false) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, rememberMe }),
    });
    if (!response.ok) {
      throw await buildApiError(response, 'Login failed. Please try again.');
    }
    const data = await response.json();
    if (data.accessToken) {
      _accessToken = data.accessToken;
      // Store user data in sessionStorage (safer than localStorage)
      if (data.user) {
        // isAdmin is derived from the JWT claim — not stored in sessionStorage to
        // prevent privilege escalation. Session storage is cleared when tab closes.
        setCurrentUser(data.user);
      }
    }
    return data;
  },

  forgotPassword: async (email) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      throw await buildApiError(response, 'Unable to process your request right now. Please try again.');
    }
    return response.json();
  },

  resetPassword: async (token, newPassword) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Password reset failed. Please try again.');
    }

    return response.json();
  },

  verifyEmail: async (token) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Email verification failed. The link may have expired.');
    }

    return response.json();
  },

  resendVerification: async (email) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/resend-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to resend verification email. Please try again.');
    }

    return response.json();
  },

  /**
   * Phone OTP — send a one-time code via SMS AND WhatsApp (Twilio Verify).
   * Backend: POST /auth/phone/send-otp
   * Dispatches to both channels so the user receives the code on either.
   */
  sendPhoneOTP: async (phone) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/phone/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // channels array tells backend to fan-out via Twilio Verify to SMS + WhatsApp
      body: JSON.stringify({ phone, channels: ['sms', 'whatsapp'] }),
    });
    if (!response.ok) {
      throw await buildApiError(response, 'Could not send OTP. Please check the number and try again.');
    }
    return response.json();
  },

  /**
   * Phone OTP — verify the code and sign the user in.
   * Backend: POST /auth/phone/verify-otp
   * Returns { accessToken, user, isNewUser } on success.
   * isNewUser = true means the frontend should collect name + email next.
   */
  verifyPhoneOTP: async (phone, code) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/phone/verify-otp`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, code }),
    });
    if (!response.ok) {
      throw await buildApiError(response, 'Invalid or expired code. Please try again.');
    }
    const data = await response.json();
    if (data.accessToken) {
      _accessToken = data.accessToken;
      if (data.user) setCurrentUser(data.user);
    }
    return data;
  },

  /**
   * Complete phone sign-up — save full name + email for a new phone-auth user.
   * Backend: POST /auth/phone/complete-profile
   * Returns { user } with the updated profile.
   */
  completePhoneProfile: async (phone, fullName, email) => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/auth/phone/complete-profile`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, fullName, email: email || undefined }),
    });
    if (!response.ok) {
      throw await buildApiError(response, 'Could not save your profile. Please try again.');
    }
    const data = await response.json();
    if (data.user) setCurrentUser(data.user);
    return data;
  },

  logout: async () => {
    // Revoke refresh token server-side first.
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best effort — clear local state regardless.
    }
    _accessToken = null;
    setCurrentUser(null); // Clear sessionStorage
  },

  /** Attempts a silent token refresh using the HttpOnly cookie. */
  silentRefresh: () => tryRefreshAccessToken(),

  getToken: () => _accessToken,
  getUser: () => {
    const user = getCurrentUser();
    if (!user) return null;
    // Derive isAdmin from the in-memory JWT claim so sessionStorage never holds privilege info.
    if (_accessToken) {
      const claims = decodeJwtPayload(_accessToken);
      user.isAdmin = claims?.role === 'admin';
    }
    return user;
  },
};

// ── Flight result stale-cache ─────────────────────────────────────────────
// Stores the last successful API response in sessionStorage so that when the
// provider API is temporarily unavailable we can serve slightly-stale prices
// rather than showing an empty screen. Results older than CACHE_TTL_MS are
// never served as fallback. Keys are prefixed by route+date+class so that
// different searches never collide.
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours

const flightResultCache = {
  _key: (prefix, parts) => `airfly_fcache_${prefix}_${parts.join('|').toLowerCase()}`,

  set(prefix, parts, data) {
    try {
      sessionStorage.setItem(
        this._key(prefix, parts),
        JSON.stringify({ data, timestamp: Date.now() }),
      );
    } catch {
      // sessionStorage may be unavailable (private mode quota exceeded) — silently skip.
    }
  },

  get(prefix, parts) {
    try {
      const raw = sessionStorage.getItem(this._key(prefix, parts));
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.timestamp > CACHE_TTL_MS) return null;
      if (Array.isArray(entry.data)) {
        // For array results (e.g. search), attach meta as properties without breaking Array.isArray.
        const arr = [...entry.data];
        arr.isCached = true;
        arr.cachedAt = entry.timestamp;
        return arr;
      }
      return { ...entry.data, isCached: true, cachedAt: entry.timestamp };
    } catch {
      return null;
    }
  },
};
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Flights API calls
 */
export const flightsAPI = {
  search: async (from, to, date, flightClass = 'Economy', filters = {}) => {
    const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;
    const cacheKey = ['sf', from, to, dateStr, flightClass];

    const params = new URLSearchParams({
      from,
      to,
      date: dateStr,
      class: flightClass,
    });

    // Add optional filters
    if (filters.minPrice) params.append('minPrice', filters.minPrice);
    if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
    if (filters.afterTime) params.append('afterTime', filters.afterTime);
    if (filters.beforeTime) params.append('beforeTime', filters.beforeTime);
    if (filters.type) params.append('type', filters.type);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    
    // Force cache bypass when price changed
    if (filters.forceRefresh) {
      params.append('forceRefresh', 'true');
    }

    // Abort after 80 seconds — increased from 65s to better handle cold starts on AWS ECS/Lambda.
    // Gives backend (55s cap) and cold container initialization time before browser kills the request.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 80_000);

    let response;
    try {
      response = await fetchWithOfflineHint(`${API_BASE_URL}/flights/search?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        const timeoutError = new Error('Flight search is taking too long. Please try again.');
        timeoutError.isTimeout = true;
        // Try stale cache before surfacing the timeout.
        const cached = flightResultCache.get('sf', cacheKey.slice(1));
        if (cached) return cached;
        throw timeoutError;
      }
      // Network error — try stale cache.
      const cached = flightResultCache.get('sf', cacheKey.slice(1));
      if (cached) return cached;
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      // Provider error (5xx) — try stale cache before throwing.
      const cached = flightResultCache.get('sf', cacheKey.slice(1));
      if (cached) return cached;
      throw await buildApiError(response, 'Unable to search flights right now. Please try again.');
    }

    const data = await response.json();
    const results = Array.isArray(data) ? data : data.value || [];
    // Write to cache only when we have meaningful results.
    if (results.length > 0) flightResultCache.set('sf', cacheKey.slice(1), results);
    return results;
  },

  searchRoundTrip: async (from, to, outboundDate, returnDate, flightClass = 'Economy', filters = {}) => {
    const outboundStr = outboundDate instanceof Date ? outboundDate.toISOString().split('T')[0] : outboundDate;
    const returnStr = returnDate instanceof Date ? returnDate.toISOString().split('T')[0] : returnDate;

    const params = new URLSearchParams({
      from,
      to,
      outboundDate: outboundStr,
      returnDate: returnStr,
      class: flightClass,
    });

    // Pass forceRefresh flag to bypass backend cache
    if (filters.forceRefresh) {
      params.set('forceRefresh', 'true');
    }

    // Round-trip searches take longer - allow 105 seconds (backend has 90s timeout)
    // Increased to handle cold starts on AWS ECS/Lambda
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 105_000);

    let response;
    try {
      response = await fetchWithOfflineHint(`${API_BASE_URL}/flights/search-roundtrip?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        const timeoutError = new Error('Round-trip search is taking too long. Please try again.');
        timeoutError.isTimeout = true;
        // Don't attempt stale cache for round-trips (not cached to avoid quota issues)
        throw timeoutError;
      }
      // Network error - no cache fallback for round-trips
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      // Provider error - no cache fallback for round-trips
      throw await buildApiError(response, 'Unable to search round-trip flights right now. Please try again.');
    }

    const data = await response.json();
    // Backend returns RoundTripSearchResult with { Offers: [ { CombinedOfferId, OutboundFlight, ReturnFlight, TotalPrice, Currency } ] }
    
    // Don't cache round-trip results - they're too large (2x flight data + fare details)
    // and can exceed browser storage quota, causing QuotaExceededError runtime failures.
    // The backend already caches Duffel responses for 30 minutes, which is sufficient.
    
    return data;
  },

  // NEW: Parallel search returns combined offers + ALL one-way flights
  searchRoundTripWithAllOptions: async (from, to, outboundDate, returnDate, flightClass = 'Economy', filters = {}) => {
    const outboundStr = outboundDate instanceof Date ? outboundDate.toISOString().split('T')[0] : outboundDate;
    const returnStr = returnDate instanceof Date ? returnDate.toISOString().split('T')[0] : returnDate;

    const params = new URLSearchParams({
      from,
      to,
      outboundDate: outboundStr,
      returnDate: returnStr,
      class: flightClass,
    });

    if (filters.forceRefresh) {
      params.set('forceRefresh', 'true');
    }

    // 3 parallel searches = longer total time, allow 120 seconds
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120_000);

    let response;
    try {
      response = await fetchWithOfflineHint(`${API_BASE_URL}/flights/search-roundtrip-all?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        const timeoutError = new Error('Round-trip search is taking too long. Please try again.');
        timeoutError.isTimeout = true;
        throw timeoutError;
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to search round-trip flights right now. Please try again.');
    }

    const data = await response.json();
    // Returns: { combinedOffers: [...], outboundFlights: [...], returnFlights: [...], metadata: {...} }
    return data;
  },

  // Exchange rates endpoint removed for security - rates no longer exposed to clients
  // All prices from API now include markup pre-applied

  selectAiDomesticDeals: async (candidates, maxRoutes = 4, prompt = '') => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/flights/ai-domestic-deals/select`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt,
        maxRoutes,
        candidates,
      }),
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to load recommended deals right now.');
    }

    return response.json();
  },

  getLatestAiDomesticDeals: async () => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/flights/ai-domestic-deals/latest`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to load recommended deals right now.');
    }

    return response.json();
  },

  /**
   * Search cheapest flights across a 7-day window from departureStart.
   * Returns { outbound: Flight[], return: Flight[], isCached?: boolean, cachedAt?: number }
   */
  cheapestWindow: async ({ from, to, departureStart, returnStart, travelClass = 'Economy', stops = 'any' }) => {
    const cacheKey = [from, to, departureStart, returnStart || '', travelClass];
    const params = new URLSearchParams({ from, to, departureStart, class: travelClass, stops });
    if (returnStart) params.append('returnStart', returnStart);

    // Abort after 45 seconds — backend is 30s for outbound + 20s for return;
    // 45s gives enough headroom for throttled provider calls plus network overhead.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45_000);

    let response;
    try {
      response = await fetchWithOfflineHint(`${API_BASE_URL}/flights/cheapest-window?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        const timeoutError = new Error('Flight search is taking too long. Please try again.');
        timeoutError.isTimeout = true;
        const cached = flightResultCache.get('cw', cacheKey);
        if (cached) return cached;
        throw timeoutError;
      }
      const cached = flightResultCache.get('cw', cacheKey);
      if (cached) return cached;
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      // Provider/backend error — return stale cache rather than empty screen.
      const cached = flightResultCache.get('cw', cacheKey);
      if (cached) return cached;
      return { outbound: [], return: [] };
    }

    const data = await response.json();
    const outbound = data?.outbound ?? [];
    const ret = data?.return ?? [];
    // Write to cache when we have meaningful results.
    if (outbound.length > 0) flightResultCache.set('cw', cacheKey, { outbound, return: ret });
    return { outbound, return: ret };
  },

  /**
   * Warmup ping to wake up cold containers on AWS ECS/Lambda.
   * Called on SearchFlights page load to reduce first-search latency.
   * Production-only: dev environment allows cold starts to save costs.
   * Fires in background and never throws errors to the caller.
   */
  warmup: async () => {
    // Only warmup in production (api.airflynow.com), skip in dev/localhost
    const isProduction = API_BASE_URL.includes('api.airflynow.com') && !API_BASE_URL.includes('dev');
    if (!isProduction) {
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      await fetch(`${API_BASE_URL.replace('/api/v1', '')}/health`, {
        method: 'GET',
        signal: controller.signal,
      }).catch(() => {});
      
      clearTimeout(timeoutId);
    } catch {
      // Silent fail — warmup is best-effort
    }
  },
};

/**
 * Bookings API calls
 */
export const bookingsAPI = {
  /**
   * Query bookings by Stripe session ID.
   * Used to find bookings created during checkout before payment completes.
   */
  getBySessionId: async (sessionId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/by-session/${encodeURIComponent(sessionId)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to retrieve booking details.');
    }

    return response.json();
  },

  create: async (flightCode, userId, passengerName, passengerDetails = [], promoCode = null, contactDetails = null, selectedCurrency = null, offerSnapshotId = null, paymentSessionId = null) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flightCode, userId, passengerName, passengerDetails, promoCode, contactDetails, selectedCurrency, offerSnapshotId, paymentSessionId }),
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Booking failed');
    }

    return response.json();
  },

  createRoundTrip: async (outboundFlightCode, returnFlightCode, userId, passengerName, passengerDetails = [], promoCode = null, contactDetails = null, selectedCurrency = null, outboundOfferSnapshotId = null, returnOfferSnapshotId = null, paymentSessionId = null) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/round-trip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outboundFlightCode, returnFlightCode, userId, passengerName, passengerDetails, promoCode, contactDetails, selectedCurrency, outboundOfferSnapshotId, returnOfferSnapshotId, paymentSessionId }),
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Round-trip booking failed');
    }

    return response.json();
  },

  createCheckoutSession: async (flightCode, userId, passengerName, passengerDetails = [], promoCode = null, contactDetails = null, selectedCurrency = null, offerSnapshotId = null, fareType = null, bypassDuplicateCheck = false, loyaltyRedemption = null) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ flightCode, userId, passengerName, passengerDetails, promoCode, contactDetails, selectedCurrency, offerSnapshotId, fareType, bypassDuplicateCheck, loyaltyRedemption }),
    });

    if (response.status === 401) {
      const authErr = new Error('Please sign in to complete your booking.');
      authErr.status = 401;
      authErr.code = 'AUTH_REQUIRED';
      throw authErr;
    }

    if (!response.ok) {
      const errorText = await response.text();
      
      try {
        const errorData = JSON.parse(errorText);
        
        // Check for duplicate booking warning (409 Conflict)
        if (response.status === 409 && errorData.code === 'DUPLICATE_BOOKING_WARNING') {
          const err = new Error(errorData.error || 'You already have a booking for this route.');
          err.code = 'DUPLICATE_BOOKING_WARNING';
          err.status = response.status;
          err.details = errorData;
          err.requiresBypass = errorData.requiresBypass;
          err.route = errorData.route;
          throw err;
        }
        
        // Check for OFFER_EXPIRED or message indicating unavailability
        if (errorData.code === 'OFFER_EXPIRED' || errorData.code === 'OFFER_EXPIRED_VALIDATION' ||
            (errorData.error && errorData.error.toLowerCase().includes('no longer available'))) {
          const err = new Error(errorData.error || 'This flight offer has expired.');
          err.code = errorData.code || 'OFFER_EXPIRED'; // Preserve original code from backend
          err.status = response.status;
          err.details = errorData;
          err.routeContext = errorData.routeContext; // Preserve route context for repricing
          throw err;
        }
        // Re-throw with parsed error message if available
        if (errorData.error) {
          const err = new Error(errorData.error);
          err.status = response.status;
          err.code = errorData.code;
          throw err;
        }
      } catch (parseError) {
        if (parseError.code === 'OFFER_EXPIRED') {
          throw parseError;
        }
        // If it's an error we threw, re-throw it
        if (parseError.status) {
          throw parseError;
        }
        // JSON parse failure - use generic error
        // (parseError is intentionally not logged)
      }
      
      // Use user-friendly error message based on HTTP status
      const err = new Error(getFriendlyErrorMessage(response.status));
      err.status = response.status;
      throw err;
    }

    return response.json();
  },

  createRoundTripCheckoutSession: async (outboundFlightCode, returnFlightCode, userId, passengerName, passengerDetails = [], promoCode = null, contactDetails = null, selectedCurrency = null, outboundOfferSnapshotId = null, returnOfferSnapshotId = null, fareType = null, bypassDuplicateCheck = false, combinedOfferId = null, loyaltyRedemption = null) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/checkout-session/round-trip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outboundFlightCode, returnFlightCode, userId, passengerName, passengerDetails, promoCode, contactDetails, selectedCurrency, outboundOfferSnapshotId, returnOfferSnapshotId, fareType, bypassDuplicateCheck, combinedOfferId, loyaltyRedemption }),
    });

    if (response.status === 401) {
      const authErr = new Error('Please sign in to complete your booking.');
      authErr.status = 401;
      authErr.code = 'AUTH_REQUIRED';
      throw authErr;
    }

    if (!response.ok) {
      const errorText = await response.text();
      
      try {
        const errorData = JSON.parse(errorText);
        
        // Check for duplicate booking warning (409 Conflict)
        if (response.status === 409 && errorData.code === 'DUPLICATE_BOOKING_WARNING') {
          const err = new Error(errorData.error || 'You already have a booking for this route.');
          err.code = 'DUPLICATE_BOOKING_WARNING';
          err.status = response.status;
          err.details = errorData;
          err.requiresBypass = errorData.requiresBypass;
          err.duplicateLegs = errorData.duplicateLegs;
          throw err;
        }
        
        // Check for OFFER_EXPIRED or message indicating unavailability
        if (errorData.code === 'OFFER_EXPIRED' || errorData.code === 'OFFER_EXPIRED_VALIDATION' ||
            (errorData.error && errorData.error.toLowerCase().includes('no longer available'))) {
          const err = new Error(errorData.error || 'This flight offer has expired.');
          err.code = errorData.code || 'OFFER_EXPIRED'; // Preserve original code from backend
          err.status = response.status;
          err.details = errorData;
          err.routeContext = errorData.routeContext; // Preserve route context for repricing
          throw err;
        }
        // Re-throw with parsed error message if available
        if (errorData.error) {
          const err = new Error(errorData.error);
          err.status = response.status;
          err.code = errorData.code;
          throw err;
        }
      } catch (parseError) {
        if (parseError.code === 'OFFER_EXPIRED' || parseError.code === 'OFFER_EXPIRED_VALIDATION') {
          throw parseError;
        }
        // If it's an error we threw, re-throw it
        if (parseError.status) {
          throw parseError;
        }
        // JSON parse failure - use generic error
        // (parseError is intentionally not logged)
      }
      
      // Use user-friendly error message based on HTTP status
      const err = new Error(getFriendlyErrorMessage(response.status));
      err.status = response.status;
      throw err;
    }

    return response.json();
  },

  confirmDuffelPayment: async (paymentIntentId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/payments/duffel/confirm-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ PaymentIntentId: paymentIntentId }), // Backend expects PascalCase
    });

    if (!response.ok) {
      const errorText = await response.text();
      
      try {
        const errorData = JSON.parse(errorText);
        const err = new Error(errorData.error || 'Failed to confirm payment');
        err.status = response.status;
        throw err;
      } catch (parseError) {
        if (parseError.status) {
          throw parseError;
        }
        // JSON parse failure - use generic error
        const err = new Error('Failed to confirm payment. Please contact support.');
        err.status = response.status;
        throw err;
      }
    }

    return response.json();
  },

  validateOffer: async (offerSnapshotId, passengerCount) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/validate-offer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ offerSnapshotId, passengerCount }),
    });

    if (!response.ok) {
      // Offer validation error
      const errorText = await response.text();
      
      try {
        const errorData = JSON.parse(errorText);
        
        // Handle specific error codes
        if (errorData.code === 'OFFER_EXPIRED') {
          const err = new Error(errorData.error || 'This flight offer has expired.');
          err.code = 'OFFER_EXPIRED';
          err.status = response.status;
          throw err;
        }
        
        // Handle 404 - offer not found in database
        if (response.status === 404) {
          const err = new Error(errorData.message || 'Offer not found.');
          err.code = 'OFFER_NOT_FOUND';
          err.status = 404;
          throw err;
        }
        
        // Handle 401 - unauthorized/session expired
        if (response.status === 401) {
          const err = new Error('Session expired. Please log in again.');
          err.code = 'UNAUTHORIZED';
          err.status = 401;
          throw err;
        }
      } catch (parseError) {
        // If it's an error we threw, re-throw it
        if (parseError.code) {
          throw parseError;
        }
      }
      
      // Generic error fallback
      const err = new Error('Unable to validate offer. Please try again.');
      err.status = response.status;
      throw err;
    }

    return response.json();
  },

  /**
   * Check if two offer snapshots (outbound + return) can be booked together as a combined round-trip.
   * Returns { combinable: boolean, combinedOfferId: string|null, totalPrice: decimal|null, message: string }.
   * If combinable=true, both flights share the same Duffel combined offer and can be booked atomically.
   * If combinable=false, flights must be booked as two separate one-way bookings.
   */
  findCombinedOffer: async (outboundOfferSnapshotId, returnOfferSnapshotId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/find-combined-offer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ outboundOfferSnapshotId, returnOfferSnapshotId }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        const err = new Error('Session expired. Please log in again.');
        err.code = 'UNAUTHORIZED';
        err.status = 401;
        throw err;
      }

      if (response.status === 400) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.error || 'Invalid offer snapshot IDs.');
        err.code = 'BAD_REQUEST';
        err.status = 400;
        throw err;
      }

      const err = new Error('Unable to check combined offer availability.');
      err.status = response.status;
      throw err;
    }

    return response.json();
  },

  /**
   * Fetches current pricing for a flight route without needing an old snapshot.
   * Returns { newAmountAud, newSnapshotId, newFlightCode }.
   * Used by BookingPreview to get fresh pricing when offers expire.
   */
  repriceOffer: async (from, to, date, travelClass = 'Economy') => {
    const params = new URLSearchParams();
    params.set('from', from);
    params.set('to', to);
    params.set('date', date);
    params.set('travelClass', travelClass);
    
    const response = await fetchWithOfflineHint(
      `${API_BASE_URL}/bookings/reprice-offer?${params.toString()}`,
    );
    if (!response.ok) {
      if (response.status === 404) {
        const data = await response.json().catch(() => ({}));
        const err = new Error(data.error || 'We\'re working to bring this route into our system. Please check back soon or try a different route.');
        err.status = 404;
        err.code = data.code || 'NO_FLIGHTS_AVAILABLE';
        throw err;
      }
      const err = new Error('Unable to fetch updated pricing.');
      err.status = response.status;
      throw err;
    }
    return response.json();
  },

  getMyBookings: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/me`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('Unauthorized');
      }
      throw await buildApiError(response, 'Unable to load bookings right now. Please try again shortly.');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.value || [];
  },

  getSurchargeSettings: async () => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/bookings/surcharge-settings`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      // Return default values if API call fails
      return { enabled: true, rate: 0.05 };
    }
    return response.json();
  },

  cancelBooking: async (bookingId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/${bookingId}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to cancel this booking right now.');
    }

    return response.json();
  },

  getFulfillmentStatus: async (bookingIds = []) => {
    if (!bookingIds.length) return [];
    const ids = bookingIds.join(',');
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/fulfillment-status?ids=${encodeURIComponent(ids)}`, {
      method: 'GET',
    });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  },

  refreshPnr: async (bookingId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/${bookingId}/refresh-pnr`, {
      method: 'POST',
    });
    if (!response.ok) throw await buildApiError(response, 'Unable to refresh booking reference.');
    return response.json();
  },

  getPassengerProfiles: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/passenger-profiles`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to load saved travellers right now.');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.value || [];
  },

  savePassengerProfiles: async (passengerDetails = []) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/passenger-profiles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ passengerDetails }),
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to save traveller details right now.');
    }

    return response.json();
  },

  getPromoOffers: async () => {
    const response = await fetchWithOfflineHint(`${API_BASE_URL}/bookings/promo-offers`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Unable to load promo offers right now.');
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.value || [];
  },

  applyPromoCode: async (
    flightCode,
    passengerCount,
    code,
    offerSnapshotId = null,
    returnFlightCode = null,
    returnOfferSnapshotId = null,
    subtotal = null,
  ) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/promo/apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        flightCode,
        passengerCount,
        code,
        offerSnapshotId,
        returnFlightCode,
        returnOfferSnapshotId,
        subtotal,
      }),
    });

    if (!response.ok) {
      throw await buildApiError(response, 'Could not apply promo code.');
    }

    return response.json();
  },

  /**
   * Download booking details as PDF
   */
  downloadBookingDetails: async (bookingId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/${bookingId}/download-details`, {
      method: 'GET',
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in to download booking details.');
      }
      const errorText = await response.text();
      throw new Error(errorText || 'Unable to download booking details.');
    }

    return response.blob();
  },

  /**
   * Download payment receipt as PDF
   */
  downloadPaymentReceipt: async (bookingId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/${bookingId}/download-payment`, {
      method: 'GET',
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Please sign in to download payment receipt.');
      }
      const errorText = await response.text();
      throw new Error(errorText || 'Unable to download payment receipt.');
    }

    return response.blob();
  },
};

const apiClient = {
  authAPI,
  flightsAPI,
  bookingsAPI,
};

/**
 * Platform stats — used by SocialProofBar to display live numbers
 */
export const statsAPI = {
  getPlatformStats: async () => {
    try {
      const response = await fetchWithOfflineHint(`${API_BASE_URL}/bookings/platform-stats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },
};

export const rescheduleAPI = {
  getChangeOptions: async (bookingId, newDate, cabinClass = null) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/${bookingId}/change-options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newDate, cabinClass }),
    });
    if (!response.ok) throw await buildApiError(response, 'Could not fetch reschedule options.');
    return response.json();
  },

  initiateCheckout: async (bookingId, offerData) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/${bookingId}/change-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(offerData),
    });
    if (!response.ok) throw await buildApiError(response, 'Could not initiate reschedule checkout.');
    return response.json();
  },

  confirmAfterPayment: async (bookingId, sessionId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/bookings/${bookingId}/reschedule-confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId }),
    });
    if (!response.ok) throw await buildApiError(response, 'Could not confirm reschedule.');
    return response.json();
  },
};

/**
 * AirFlyNow Loyalty & Referral API
 * Security: All endpoints require authentication
 * Validation: Max 1000 points per redemption, balance checks, prevents misuse
 */
export const loyaltyAPI = {
  /**
   * Get user's loyalty wallet with point balances
   * Returns: { totalPoints, availablePoints, usedPoints, availableValueAud, maxRedeemablePoints, walletStatus }
   */
  getWallet: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/loyalty/wallet`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      if (response.status === 401) {
        const err = new Error('Please sign in to view your wallet.');
        err.status = 401;
        err.code = 'AUTH_REQUIRED';
        throw err;
      }
      throw await buildApiError(response, 'Unable to load wallet. Please try again.');
    }
    return response.json();
  },

  /**
   * Get transaction history with pagination
   * Returns: { transactions: [], totalCount, limit, offset }
   */
  getTransactions: async (limit = 20, offset = 0) => {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    const response = await fetchWithAuth(`${API_BASE_URL}/loyalty/transactions?${params}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      if (response.status === 401) {
        const err = new Error('Please sign in to view transaction history.');
        err.status = 401;
        err.code = 'AUTH_REQUIRED';
        throw err;
      }
      throw await buildApiError(response, 'Unable to load transactions.');
    }
    return response.json();
  },

  /**
   * Redeem points during checkout to reduce booking cost
   * Request: { pointsToRedeem, bookingId }
   * Returns: { success, pointsRedeemed, discountAmount, currency, message }
   */
  applyPoints: async (pointsToRedeem, bookingId) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/loyalty/apply-points`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pointsToRedeem, bookingId }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        const err = new Error('Please sign in to redeem points.');
        err.status = 401;
        err.code = 'AUTH_REQUIRED';
        throw err;
      }
      if (response.status === 400) {
        const err = new Error(errorData.error || 'Unable to redeem points.');
        err.status = 400;
        err.code = 'INVALID_REDEMPTION';
        throw err;
      }
      throw await buildApiError(response, 'Points redemption failed. Please try again.');
    }
    return response.json();
  },

  /**
   * Get or create user's referral code
   * Returns: { referralCode, shareUrl, totalReferrals, completedReferrals, pendingReferrals, totalPointsEarned, shareMessage }
   */
  getReferralCode: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/loyalty/referral-code`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      if (response.status === 401) {
        const err = new Error('Please sign in to view your referral code.');
        err.status = 401;
        err.code = 'AUTH_REQUIRED';
        throw err;
      }
      throw await buildApiError(response, 'Unable to load referral code.');
    }
    return response.json();
  },

  /**
   * Generate a new referral entry (optional: pre-fill email)
   * Request: { referredEmail? }
   * Returns: { referralCode, shareUrl }
   */
  generateReferral: async (referredEmail = null) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/loyalty/referral-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referredEmail }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        const err = new Error('Please sign in to generate referral.');
        err.status = 401;
        err.code = 'AUTH_REQUIRED';
        throw err;
      }
      throw await buildApiError(response, 'Unable to generate referral.');
    }
    return response.json();
  },

  /**
   * Get referral statistics
   * Returns: { totalReferrals, completedReferrals, pendingReferrals, totalPointsEarned, totalValueEarned }
   */
  getReferralStats: async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/loyalty/referral-stats`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      if (response.status === 401) {
        const err = new Error('Please sign in to view referral stats.');
        err.status = 401;
        err.code = 'AUTH_REQUIRED';
        throw err;
      }
      throw await buildApiError(response, 'Unable to load referral stats.');
    }
    return response.json();
  },

  /**
   * Link referral code to user account during signup
   * Request: { referralCode }
   * Returns: { success, message }
   */
  linkReferral: async (referralCode) => {
    const response = await fetchWithAuth(`${API_BASE_URL}/loyalty/link-referral`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ referralCode }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401) {
        const err = new Error('Please sign in to link referral code.');
        err.status = 401;
        err.code = 'AUTH_REQUIRED';
        throw err;
      }
      if (response.status === 400) {
        const err = new Error(errorData.error || 'Invalid referral code.');
        err.status = 400;
        err.code = 'INVALID_CODE';
        throw err;
      }
      throw await buildApiError(response, 'Unable to link referral code.');
    }
    return response.json();
  },
};

export default apiClient;
